// src/store/slices/profileSlice.ts - Profile state management

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  serverTimestamp 
} from 'firebase/firestore';
import { firestore } from '../../firebase/config';

// Define types
interface Profile {
  userId: string;
  coreValues: string[];
  painPoints: string[];
  lifeThemes: string[];
  customFields: Record<string, any>;
  createdAt: number;
  updatedAt: number;
}

interface ProfileState {
  profile: Profile | null;
  isLoading: boolean;
  error: string | null;
  assessmentComplete: boolean;
}

// Initial state
const initialState: ProfileState = {
  profile: null,
  isLoading: false,
  error: null,
  assessmentComplete: false,
};

// Async thunks
export const fetchProfile = createAsyncThunk(
  'profile/fetchProfile',
  async (userId: string, { rejectWithValue }) => {
    try {
      const profileRef = doc(firestore, 'profiles', userId);
      const profileSnap = await getDoc(profileRef);
      
      if (profileSnap.exists()) {
        return profileSnap.data() as Profile;
      } else {
        return null;
      }
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const createProfile = createAsyncThunk(
  'profile/createProfile',
  async (profileData: Partial<Profile>, { rejectWithValue }) => {
    try {
      if (!profileData.userId) {
        return rejectWithValue('User ID is required');
      }
      
      const userId = profileData.userId;
      const profileRef = doc(firestore, 'profiles', userId);
      
      const timestamp = Date.now();
      const newProfile: Profile = {
        userId,
        coreValues: profileData.coreValues || [],
        painPoints: profileData.painPoints || [],
        lifeThemes: profileData.lifeThemes || [],
        customFields: profileData.customFields || {},
        createdAt: timestamp,
        updatedAt: timestamp,
      };
      
      await setDoc(profileRef, newProfile);
      return newProfile;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateProfile = createAsyncThunk(
  'profile/updateProfile',
  async ({ userId, updates }: { userId: string; updates: Partial<Profile> }, { rejectWithValue }) => {
    try {
      const profileRef = doc(firestore, 'profiles', userId);
      
      // Add timestamp
      const updatedData = {
        ...updates,
        updatedAt: Date.now(),
      };
      
      await updateDoc(profileRef, updatedData);
      return updatedData;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// Create slice
const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    clearProfile: (state) => {
      state.profile = null;
      state.isLoading = false;
      state.error = null;
      state.assessmentComplete = false;
    },
    setAssessmentComplete: (state, action) => {
      state.assessmentComplete = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Fetch profile
    builder.addCase(fetchProfile.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchProfile.fulfilled, (state, action) => {
      state.isLoading = false;
      state.profile = action.payload;
      state.assessmentComplete = !!action.payload;
    });
    builder.addCase(fetchProfile.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
    
    // Create profile
    builder.addCase(createProfile.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(createProfile.fulfilled, (state, action) => {
      state.isLoading = false;
      state.profile = action.payload;
      state.assessmentComplete = true;
    });
    builder.addCase(createProfile.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
    
    // Update profile
    builder.addCase(updateProfile.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(updateProfile.fulfilled, (state, action) => {
      state.isLoading = false;
      if (state.profile) {
        state.profile = {
          ...state.profile,
          ...action.payload,
        };
      }
    });
    builder.addCase(updateProfile.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
  },
});

export const { clearProfile, setAssessmentComplete } = profileSlice.actions;
export default profileSlice.reducer;
